<template>
	<div class="flex flex-col gap-4 mb-10">
		<div>
			<h1
				class="text-page-title lg:text-page-title-lg mb-4 font-sans font-medium"
			>
				{{ currentCategory?.Name }}
			</h1>
			<span class="max-w-xl inline">
				{{ stripHtmlTags(shortDescription) }}
			</span>
			<a
				v-if="currentCategory?.Description?.length > 300"
				href="#"
				class="inline-block text-sm"
				@click.prevent="toggleShowMore"
			>
				{{
					isExpanded
						? "&nbsp; &uarr; " + $t("Show less")
						: "&darr; " + $t("Show more")
				}}
			</a>
			<div v-if="storyData?.top_blocks">
				<BlokPriorityScope
					v-for="(blok, index) in storyData.top_blocks"
					:key="blok._uid"
					:priority="index <= priorityIndex"
				>
					<StoryblokComponent :blok="blok" />
				</BlokPriorityScope>
			</div>
			<div
				v-if="subCategories?.length"
				class="lg:mb-16 mb-2 overflow-x-auto whitespace-nowrap pt-4"
			>
				<div class="flex w-max gap-2 pb-4">
					<!-- Link is the whole box: Button pads a wrapper div outside the <a>. -->
					<NuxtLink
						v-for="subCategory in subCategories"
						:key="subCategory.CategoryId"
						:to="
							localePath(`/${createCategoryLink({ category: subCategory })}`)
						"
						class="button button-primary h-[42px] flex items-center justify-center px-5"
					>
						{{ subCategory.Name }}
					</NuxtLink>
				</div>
			</div>
			<ProductRecommendations
				v-if="currentCategory?.Level === 1 && recommendations"
				:blok="recommendations"
				:hierarchies="[[currentCategory.Name]]"
				title-styles="block text-center pb-5 border-b"
				class="mb-5 grid grid-cols-1"
			/>
		</div>
		<div class="flex flex-col lg:flex-row justify-between w-full">
			<ProductListing
				:items="combinedItems"
				:item-count="itemCount"
				:page-size="currentPageSize"
				:picture-aspect-ratio="pictureAspectRatio"
				:current-page="currentPage"
				:is-loaded="isLoaded"
				:category-name="currentCategory?.Name"
				:all-parametrics="allParametrics"
				show-variants
			/>
		</div>

		<div v-if="storyData?.bottom_blocks">
			<StoryblokComponent
				v-for="blok in storyData.bottom_blocks"
				:key="blok._uid"
				:blok="blok"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { lastPriorityIndex } from "~/layers/base/utils/imagePriority";
import { computed, ref, toRef } from "vue";
import type {
	CreateFilterText,
	FilteredProductItem
} from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import type { Category } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import type { ParametricListValue } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import ProductListing from "./ProductListing.vue";
import type { CombinedItem, InitialData } from "~/types/components/category";
import type {
	AdvertisementBlok,
	BlokColumns,
	BlokProductRecommendations,
	CategoryPage
} from "~/types/storyblok/storyblok";
import { getFiltersFromUrl } from "~/utils/categories/getFiltersFromUrl";
import { mergeWithAdvertisements } from "~/utils/categories/mergeWithAdvertisements";
import { CategoryUrlParams, activeStatusID } from "~/utils/constants";
import { DEFAULT_PRODUCT_SORT } from "~/utils/product/sort";

interface Props {
	currentCategory: Category;
	initialData: InitialData & { allParametrics?: ParametricListValue[] };
	pageSize: string;
	pictureAspectRatio: string;
	initialCurrentSortingOrder: string;
	preFooter?: BlokColumns[];
	productsPopulationConfig: {
		[key: string]: string | number;
	};
	storyData?: CategoryPage;
	advertisement?: AdvertisementBlok;
	recommendations?: BlokProductRecommendations;
	initialPageNo?: number;
}

const {
	currentCategory,
	initialData,
	pageSize,
	pictureAspectRatio,
	initialCurrentSortingOrder,
	productsPopulationConfig,
	storyData,
	advertisement,
	initialPageNo = 1
} = defineProps<Props>();

const priorityIndex = computed(() => lastPriorityIndex(storyData?.top_blocks));

const { listProductByFilters, createFilterText, helloRetailApi } =
	useNorcePwa();
const norceStore = useNorceStore();
const { fetchAllParametrics } = useAllParametrics();
const {
	public: {
		norce: { isHelloRetailTracking }
	}
} = useRuntimeConfig();

const { createCategoryLink } = useCategory();
const localePath = useLocalePath();

const route = useRoute();

const initialDataRef = toRef(() => initialData);
const currentCategoryRef = toRef(() => currentCategory);

const currentSortingOrder = ref(initialCurrentSortingOrder);
const currentPageSize = ref(pageSize);
const subCategories = ref(currentCategoryRef.value?.Children);
const allParametrics = ref<ParametricListValue[]>(
	initialData?.allParametrics || []
);

const selectedFilters = ref<CreateFilterText>(
	!route.query.filter
		? {
				allParametrics: [],
				selectedFilters: {}
			}
		: getFiltersFromUrl(route.query.filter as string)
);

const buildFetchParams = (filterText: string, page: number) => {
	return {
		statusSeed: String(activeStatusID),
		...productsPopulationConfig,
		filter: filterText,
		sort: currentSortingOrder.value,
		pageNo: String(page),
		pageSize: currentPageSize.value,
		asVariants: true,
		processColorVariants: true,
		filterByAllowedColors: true,
		...norceStore.storeContext
	};
};

const fetchPage = async (page: number) => {
	const filterText = await createFilterText(selectedFilters.value);
	return listProductByFilters(buildFetchParams(filterText, page));
};

const {
	currentPage,
	currentItems,
	itemCount,
	isLoaded,
	resetToFirstPage,
	applyResults,
	syncUrl
} = useCategoryPagination({
	initialPageNo,
	initialItems:
		(initialDataRef.value.products?.Items as FilteredProductItem[]) ?? [],
	initialItemCount: initialDataRef.value.products?.ItemCount ?? 0,
	pageSize: currentPageSize,
	fetchPage
});

const productItems = computed<CombinedItem[]>(() => {
	const items = currentItems.value || [];

	return items.map((item) => ({
		type: "product" as const,
		id: `${item.Id}`,
		data: item,
		variants: item.variants
	})) as CombinedItem[];
});

const combinedItems = computed<CombinedItem[]>(() => {
	const advertisementConfig = storyData?.advertisement
		? storyData.advertisement[0]
		: advertisement;
	return mergeWithAdvertisements(productItems.value, advertisementConfig);
});

const stripHtmlTags = (html: string) => {
	if (!html) return "";
	return html.replace(/<\/?[^>]+(>|$)/g, "");
};

const buildQueryParams = (
	params: ReturnType<typeof buildFetchParams>,
	urlPage: number
): Record<string, string> => {
	const queryParams: Record<string, string> = Object.fromEntries(
		Object.entries(params)
			.filter(Boolean)
			.filter(
				([key, value]) =>
					value &&
					(key === CategoryUrlParams.FILTER ||
						(key === CategoryUrlParams.SORT && value !== DEFAULT_PRODUCT_SORT))
			)
	);
	if (urlPage > 1) queryParams.p = urlPage.toString();
	return queryParams;
};

const sendSelectedFilters = async (
	ref: CreateFilterText,
	_resetPageNo?: { resetPageNo: boolean }
) => {
	isLoaded.value = false;
	resetToFirstPage();

	const fetchedParametrics = await fetchAllParametrics();
	ref.allParametrics = fetchedParametrics;
	allParametrics.value = fetchedParametrics;
	const filterText = await createFilterText(ref);

	const params = buildFetchParams(filterText, 1);
	syncUrl(buildQueryParams(params, 1));

	selectedFilters.value = ref;
	const data = await listProductByFilters(params);

	applyResults(data);

	isLoaded.value = true;
};

const isExpanded = ref(false);

const shortDescription = computed(() => {
	const description = currentCategory?.Description || "";
	if (isExpanded.value || description.length < 300) {
		return description;
	} else {
		return description.substring(0, 300) + "... ";
	}
});

const toggleShowMore = () => {
	isExpanded.value = !isExpanded.value;
};

onMounted(() => {
	if (!isHelloRetailTracking) return;

	helloRetailApi.track({
		serviceType: "pageview",
		trackingData: {
			location: window.location.href,
			referrer: window.location.origin,
			url: window.location.href
		}
	});
});

const pageSizeCookie = useCookie<string | null>("productsPerPage", {
	maxAge: 60 * 60 * 24 * 365,
	sameSite: "lax"
});

watch(currentPageSize, async (newSize) => {
	pageSizeCookie.value = newSize;
	await sendSelectedFilters(selectedFilters.value);
});
</script>
